
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/**
 * Normalize a company name for better matching
 * @param name The company name to normalize
 * @returns Normalized company name
 */
export const normalizeCompanyName = (name: string): string => {
  if (!name) return '';
  
  // Convert to lowercase
  let normalized = name.toLowerCase();
  
  // Remove common company suffixes and terms
  const suffixes = [' inc', ' llc', ' ltd', ' limited', ' corp', ' corporation', ' incorporated', ' company', ' co', ' group'];
  suffixes.forEach(suffix => {
    if (normalized.endsWith(suffix)) {
      normalized = normalized.slice(0, normalized.length - suffix.length);
    }
  });
  
  // Remove special characters and extra spaces
  normalized = normalized.replace(/[^\w\s]/g, '').trim();
  
  return normalized;
};

/**
 * Find an existing company by name or create a new one
 * @param companyName The company name to search for
 * @returns The company ID
 */
export const findOrCreateCompany = async (companyName: string): Promise<number | null> => {
  if (!companyName || companyName.trim() === "") return null;
  
  try {
    // First try to find a company with the normalized name
    const normalizedName = normalizeCompanyName(companyName.trim());
    
    // Look for existing company with similar name
    const { data: existingCompany, error: searchError } = await supabase
      .from('companies')
      .select('id')
      .eq('normalized_name', normalizedName)
      .limit(1)
      .single();
      
    if (!searchError && existingCompany) {
      console.log(`Found existing company with ID: ${existingCompany.id}`);
      return existingCompany.id;
    }
    
    // If not found, create a new company
    const { data: newCompany, error: insertError } = await supabase
      .from('companies')
      .insert({
        company_name: companyName.trim(),
        normalized_name: normalizedName,
        create_date: new Date().toISOString() // Fix: convert Date to string
      })
      .select('id')
      .single();
      
    if (insertError) {
      console.error("Error creating company:", insertError);
      return null;
    }
    
    console.log(`Created new company with ID: ${newCompany.id}`);
    return newCompany.id;
  } catch (error) {
    console.error("Exception in findOrCreateCompany:", error);
    return null;
  }
};

/**
 * Link a contact to a company
 * @param contactId The contact ID
 * @param companyId The company ID
 * @returns Boolean indicating success
 */
export const linkContactToCompany = async (contactId: number, companyId: number): Promise<boolean> => {
  try {
    // Make sure both IDs are numbers to avoid type errors
    const numericContactId = Number(contactId);
    const numericCompanyId = Number(companyId);
    
    console.log(`Linking contact ${numericContactId} to company ${numericCompanyId}`);
    
    // Update contact with company ID
    const { error: updateError } = await supabase
      .from('connections_new')
      .update({ company_id: numericCompanyId })
      .eq('id', numericContactId);
      
    if (updateError) {
      console.error("Error updating contact with company ID:", updateError);
      return false;
    }
    
    console.log(`Updated contact ${numericContactId} with company ID ${numericCompanyId}`);
    
    // Check if there's already an entry in the company_contacts table
    const { data: existingLink, error: checkError } = await supabase
      .from('company_contacts')
      .select('id')
      .eq('company_id', numericCompanyId)
      .eq('contact_id', numericContactId)
      .maybeSingle();
      
    if (checkError) {
      console.error("Error checking for existing company contact link:", checkError);
    }
    
    // If no existing link, create one in the company_contacts join table
    if (!existingLink) {
      const { error: joinError } = await supabase
        .from('company_contacts')
        .insert({
          company_id: numericCompanyId,
          contact_id: numericContactId,
          is_primary: true // Assuming the contact is a primary contact by default
        });
        
      if (joinError) {
        console.error("Error creating company_contacts entry:", joinError);
        // Don't return false here, as we've already updated the contact
      } else {
        console.log(`Created company_contacts entry for contact ${numericContactId} and company ${numericCompanyId}`);
      }
    }
    
    return true;
  } catch (error) {
    console.error("Error in linkContactToCompany:", error);
    return false;
  }
};

/**
 * Auto-link a contact to a company based on their current employer
 * @param contactId The contact ID to process
 * @returns Boolean indicating success
 */
export const autoLinkContactToCompany = async (contactId: number): Promise<boolean> => {
  try {
    // Make sure contactId is a number
    const numericContactId = Number(contactId);
    
    // Get the contact's current employer
    const { data: contact, error: contactError } = await supabase
      .from('connections_new')
      .select('current_employer, company_id')
      .eq('id', numericContactId)
      .single();
      
    if (contactError || !contact) {
      console.error("Error fetching contact:", contactError);
      return false;
    }
    
    // If the contact already has a company_id, log and return true
    if (contact.company_id) {
      console.log(`Contact ${numericContactId} already has company_id ${contact.company_id}`);
      return true;
    }
    
    if (!contact.current_employer) {
      console.log(`Contact ${numericContactId} has no current employer, skipping company linking`);
      return false;
    }
    
    // Find or create company based on current employer
    const companyId = await findOrCreateCompany(contact.current_employer);
    
    if (!companyId) {
      console.log(`Could not find or create company for employer: ${contact.current_employer}`);
      return false;
    }
    
    // Link the contact to the company
    return await linkContactToCompany(numericContactId, companyId);
  } catch (error) {
    console.error("Error in autoLinkContactToCompany:", error);
    return false;
  }
};

/**
 * Run a batch process to link contacts to companies based on their current employer
 * @param batchSize Number of contacts to process in each batch
 * @param onProgress Callback to update progress
 * @returns Statistics about the operation
 */
export const batchLinkContactsToCompanies = async (
  batchSize: number = 50,
  onProgress?: (processed: number, total: number) => void
): Promise<{ total: number, linked: number, errors: number }> => {
  let stats = {
    total: 0,
    linked: 0,
    errors: 0
  };
  
  try {
    // Get count of contacts with currentEmployer but no companyId
    const { count, error: countError } = await supabase
      .from('connections_new')
      .select('*', { count: 'exact', head: true })
      .not('current_employer', 'is', null)
      .is('company_id', null);
      
    if (countError) {
      console.error("Error counting contacts:", countError);
      return stats;
    }
    
    const total = count || 0;
    stats.total = total;
    
    if (total === 0) {
      console.log("No contacts found that need to be linked to companies");
      return stats;
    }
    
    console.log(`Found ${total} contacts that need to be linked to companies`);
    
    // Process in batches
    for (let offset = 0; offset < total; offset += batchSize) {
      // Fetch batch of contacts
      const { data: contacts, error: fetchError } = await supabase
        .from('connections_new')
        .select('id, current_employer')
        .not('current_employer', 'is', null)
        .is('company_id', null)
        .range(offset, offset + batchSize - 1);
        
      if (fetchError) {
        console.error(`Error fetching contacts batch at offset ${offset}:`, fetchError);
        stats.errors += batchSize;
        continue;
      }
      
      // Process each contact in the batch
      for (const contact of contacts || []) {
        if (contact.current_employer) {
          const companyId = await findOrCreateCompany(contact.current_employer);
          
          if (companyId) {
            const success = await linkContactToCompany(contact.id, companyId);
            if (success) {
              stats.linked++;
            } else {
              stats.errors++;
            }
          } else {
            stats.errors++;
          }
        }
      }
      
      // Update progress
      if (onProgress) {
        onProgress(Math.min(offset + batchSize, total), total);
      }
    }
    
    return stats;
  } catch (error) {
    console.error("Error in batchLinkContactsToCompanies:", error);
    return stats;
  }
};

/**
 * Check for inconsistencies between currentEmployer and companyId
 * @returns Array of contacts with inconsistencies
 */
export const findCompanyInconsistencies = async () => {
  try {
    // Get contacts with both currentEmployer and companyId
    const { data: contacts, error } = await supabase
      .from('connections_new')
      .select(`
        id, 
        name, 
        current_employer, 
        company_id,
        company:company_id (company_name)
      `)
      .not('current_employer', 'is', null)
      .not('company_id', 'is', null);
      
    if (error) {
      console.error("Error fetching contacts for inconsistency check:", error);
      return [];
    }
    
    // Filter for inconsistencies using normalized name comparison
    const inconsistencies = contacts?.filter(contact => {
      const employerName = contact.current_employer?.trim() || '';
      const companyName = contact.company?.company_name?.trim() || '';
      
      // Skip if either is missing
      if (!employerName || !companyName) return false;
      
      // Compare using normalized names
      const normalizedEmployer = normalizeCompanyName(employerName);
      const normalizedCompany = normalizeCompanyName(companyName);
      
      // Check if normalized company name is similar to employer
      return !normalizedEmployer.includes(normalizedCompany) && 
             !normalizedCompany.includes(normalizedEmployer);
    }) || [];
    
    return inconsistencies;
  } catch (error) {
    console.error("Error in findCompanyInconsistencies:", error);
    return [];
  }
};

/**
 * Fix inconsistencies by updating company links or employer names
 * @param contactId Contact ID to fix
 * @param action 'update_company' to link to correct company or 'update_employer' to update employer name
 * @returns Boolean indicating success
 */
export const fixCompanyInconsistency = async (
  contactId: number, 
  action: 'update_company' | 'update_employer'
): Promise<boolean> => {
  try {
    // Get contact details
    const { data: contact, error: contactError } = await supabase
      .from('connections_new')
      .select(`
        id, 
        current_employer, 
        company_id,
        company:company_id (company_name)
      `)
      .eq('id', contactId)
      .single();
      
    if (contactError || !contact) {
      console.error("Error fetching contact for inconsistency fix:", contactError);
      return false;
    }
    
    if (action === 'update_company') {
      // Update company link based on current employer
      const companyId = await findOrCreateCompany(contact.current_employer);
      if (!companyId) {
        toast.error(`Could not find or create company for ${contact.current_employer}`);
        return false;
      }
      
      return await linkContactToCompany(contactId, companyId);
    } 
    else if (action === 'update_employer') {
      // Update current employer based on linked company
      if (!contact.company?.company_name) {
        toast.error("Contact has no linked company");
        return false;
      }
      
      const { error: updateError } = await supabase
        .from('connections_new')
        .update({ current_employer: contact.company.company_name })
        .eq('id', contactId);
        
      if (updateError) {
        console.error("Error updating current_employer:", updateError);
        toast.error("Failed to update current employer");
        return false;
      }
      
      toast.success("Updated current employer successfully");
      return true;
    }
    
    return false;
  } catch (error) {
    console.error("Error in fixCompanyInconsistency:", error);
    toast.error("An error occurred while fixing inconsistency");
    return false;
  }
};

/**
 * Manually link a specific contact to their company based on their current employer
 * @param contactId The specific contact ID to link
 * @returns Results of the operation
 */
export const manuallyLinkContact = async (contactId: number): Promise<{ success: boolean; message: string }> => {
  try {
    console.log(`Manually linking contact ${contactId} to company...`);
    
    // Make sure contactId is a number
    const numericContactId = Number(contactId);
    
    // First check if the contact exists
    const { data: contact, error: contactError } = await supabase
      .from('connections_new')
      .select('id, name, current_employer, company_id')
      .eq('id', numericContactId)
      .single();
      
    if (contactError) {
      console.error(`Error finding contact with ID ${numericContactId}:`, contactError);
      return { success: false, message: `Error finding contact: ${contactError.message}` };
    }
    
    if (!contact) {
      return { success: false, message: `Contact with ID ${numericContactId} not found` };
    }
    
    // If already linked, provide that information
    if (contact.company_id) {
      // Get company name
      const { data: company } = await supabase
        .from('companies')
        .select('company_name')
        .eq('id', contact.company_id)
        .single();
        
      return { 
        success: true, 
        message: `Contact "${contact.name}" already linked to company ${company?.company_name || contact.company_id}` 
      };
    }
    
    // If contact has no current employer, can't link
    if (!contact.current_employer || contact.current_employer.trim() === '') {
      return { 
        success: false, 
        message: `Contact "${contact.name}" has no current employer information` 
      };
    }
    
    // Attempt auto-linking
    const linked = await autoLinkContactToCompany(numericContactId);
    
    if (linked) {
      // Get the newly assigned company
      const { data: updatedContact } = await supabase
        .from('connections_new')
        .select('company_id')
        .eq('id', numericContactId)
        .single();
        
      // Get company name
      const { data: company } = await supabase
        .from('companies')
        .select('company_name')
        .eq('id', updatedContact?.company_id || 0)
        .single();
        
      return { 
        success: true, 
        message: `Successfully linked contact "${contact.name}" to company "${company?.company_name || contact.current_employer}"` 
      };
    } else {
      return { 
        success: false, 
        message: `Failed to link contact "${contact.name}" to company. See console for details.` 
      };
    }
  } catch (error) {
    console.error("Error in manuallyLinkContact:", error);
    return { 
      success: false, 
      message: `An error occurred: ${error instanceof Error ? error.message : 'Unknown error'}` 
    };
  }
};
